import { Button, Dropdown, DropdownItem, AptiveIcon, Radio } from '@aptive-env/storybook'
import React, { useEffect, useState } from 'react'
import Popover from 'components/shared/Popover'
import MiniCalendar from 'components/shared/MiniCalendar'
import { format, startOfToday, getDate, getDay, getDaysInMonth, getWeeksInMonth, getWeekOfMonth } from 'date-fns'
import { ICustomEventTime, ICalendarEventInterval, IEventEndAfter } from 'interface/calendar'
import { eventConstants } from 'constants/index'

const { weekDates, monthWeeks } = eventConstants

interface CustomRecurrenceProps {
  className?: string
  onClose: () => void
  onCancel: () => void
  data: ICustomEventTime
  setData: (data: ICustomEventTime) => void
  pickedDate: Date
  setDone: (value: boolean) => void
}

function classNames(...classes: (string | boolean)[]) {
  return classes.filter(Boolean).join(' ')
}

const repeatTimes = ['Day', 'Week', 'Month', 'Year', 'Days', 'Weeks', 'Months', 'Years']

const CustomRecurrence: React.FC<CustomRecurrenceProps> = (function (params) {
  const { className, onClose, data, setData, pickedDate, setDone, onCancel } = params

  const [selectedDate, setSelectedDate] = useState<number[]>(weekDates.reduce((acc: any, val, index) => {
    if (data.week_days.includes(val)) {
      acc.push(index)
    }
    return acc
  }, [])
  )

  const repeatPeriod = 0
  
  const tmp_period = repeatTimes.filter(item => item.toLowerCase().substring(0, 2).includes(data.interval.substring(0, 2)))[0]
  const [selectedPeriod, setSelectedPeriod] = useState(repeatTimes.indexOf(tmp_period))

  const [repeatNumber, setRepeatNumber] = useState(data.repeat_every)
  const [afterNumber, setAfterNumber] = useState(data.occurrences === null ? 1 : data.occurrences)

  const [checkedEnd, setCheckedEnd] = useState(data.end_after)

  const [isOpen, setIsOpen] = useState(false)

  const [isMonthlyRecurrenceOpen, setIsMonthlyRecurrenceOpen] = useState(false)
  const [weekNum, setWeekNum] = useState(data.week_num)

  const [isDatePickerShown, setDatePickerShown] = useState(false)
  const [selectedADate, setSelectedADate] = useState<Date>(data.end_date === null ? startOfToday() : new Date(`${data.end_date}T00:00:00`))

  useEffect(() => {
    if(repeatNumber > 1 && selectedPeriod < 4) setSelectedPeriod(s => s + 4)
    if(repeatNumber === 1 && selectedPeriod > 3) setSelectedPeriod(s => s - 4)
  }, [repeatNumber, selectedPeriod])

  const handleSelect = (index: number) => () => {
    setSelectedPeriod(index)
    setIsOpen(!isOpen)
  }

  const handleMonthlyRecurrenceSelected = (value: undefined | number) => () => {
    setWeekNum(value)
    setIsMonthlyRecurrenceOpen(!isMonthlyRecurrenceOpen)
  }

  const handleSelectEndType = (value: IEventEndAfter) => {
    setCheckedEnd(value)
  }

  const handleSelectTimes = () => {
    if(repeatNumber === 1) {
      return (
        <Dropdown isOpen={isOpen} onClose={() => setIsOpen(false)} defaultValue={repeatPeriod} className="w-full">
          {
            repeatTimes.map((rp, index) => (
              index < 4 && <DropdownItem key={index} selected={selectedPeriod === index} onClick={handleSelect(index)}>
                {rp}
              </DropdownItem> 
            )) 

          }
        </Dropdown>
      )
    }
    if(repeatNumber > 1) {
      return (
        <Dropdown isOpen={isOpen} onClose={() => setIsOpen(false)} defaultValue={repeatPeriod} className="w-full">
          {
            repeatTimes.map((rp, index) => (
              index > 3 && <DropdownItem key={index} selected={selectedPeriod === index} onClick={handleSelect(index)}>
                {rp}
              </DropdownItem> 
            )) 
          }
        </Dropdown>
      )
    }
  }

  const monthlyRecurrenceDetail = (weekNum: undefined | number) => {
    if(weekNum === undefined) return 'Monthly on day ' + getDate(pickedDate)
    else if(weekNum === -1) return 'Monthly on the last ' + weekDates[getDay(pickedDate)].charAt(0).toUpperCase() + weekDates[getDay(pickedDate)].slice(1)
    else return 'Monthly on the ' + monthWeeks[weekNum] + ' ' + weekDates[getDay(pickedDate)].charAt(0).toUpperCase() + weekDates[getDay(pickedDate)].slice(1)
  }

  const dateWeeks = () => {
    const date = getDate(pickedDate)
    const dayOfWeek = getDay(pickedDate)
    const weekOfMonth = getWeekOfMonth(pickedDate)
    const weeksInMonth = getWeeksInMonth(pickedDate)
    const datesInMonth = getDaysInMonth(pickedDate)
    const actualWeekOfMonth = Math.floor(date / 7)

    const dateWeeks = []
    if (dayOfWeek === 0 && actualWeekOfMonth + 1 === weekOfMonth)
      dateWeeks.push(actualWeekOfMonth - 1)
    else
      dateWeeks.push(actualWeekOfMonth)

    if ((dayOfWeek > 0 && date + 7 > datesInMonth  && actualWeekOfMonth < weekOfMonth && weekOfMonth + 1 === weeksInMonth) || (dayOfWeek === 0 && actualWeekOfMonth + 1 === weekOfMonth && weekOfMonth === weeksInMonth)) {
      dateWeeks.push(-1)
    }

    return dateWeeks
  }

  const handleMonthlyRecurrence = () => {
    const date = getDate(pickedDate)
    const dayOfWeek = getDay(pickedDate)
    
    return (
      <Dropdown isOpen={isMonthlyRecurrenceOpen} onClose={() => setIsMonthlyRecurrenceOpen(false)} defaultValue={weekNum} className="w-full">
        <DropdownItem selected={weekNum === undefined} onClick={handleMonthlyRecurrenceSelected(undefined)}>
          Monthly on day {date}
        </DropdownItem>
        {dateWeeks().map((week) => {
          if (week !== -1) 
            return (
              <DropdownItem key={week} selected={weekNum === week} onClick={handleMonthlyRecurrenceSelected(week)}>
                Monthly on the {monthWeeks[week]} {weekDates[dayOfWeek].charAt(0).toUpperCase()}{weekDates[dayOfWeek].slice(1)}
              </DropdownItem>
            )
          else
            return (
              <DropdownItem key={week} selected={weekNum === -1} onClick={handleMonthlyRecurrenceSelected(-1)}>
                Monthly on the last {weekDates[dayOfWeek].charAt(0).toUpperCase()}{weekDates[dayOfWeek].slice(1)}
              </DropdownItem>
            )
        })}
      </Dropdown>
    )
  }

  const handleSeletedDate = (index: number) => {
    if(selectedDate.includes(index)) {
      if(selectedDate.length !== 1)
        setSelectedDate(selectedDate.filter(weekday => weekday !== index))
    } else setSelectedDate((prevState) => ([...prevState, index]))
  }

  const handleChangeSelectedDate = (newDate: Date) => {
    setSelectedADate(newDate)
    setDatePickerShown(false)
  }

  const toggleDatePickerShown = () => {
    if(checkedEnd === 'date') setDatePickerShown(prevValue => !prevValue)
  }

  const handleSetRepeatNumber = (repeatN: number, increase: boolean) => {
    if(repeatNumber > 1 && !increase) setRepeatNumber(repeatN - 1)
    if(increase) setRepeatNumber(repeatN + 1)
  }

  const passData = () => {
    let interval: ICalendarEventInterval = 'once'
    switch (selectedPeriod) {
    case 0:
    case 4:
      interval = 'daily'
      break
    case 1:
    case 5:
      interval = 'weekly'
      break
    case 2:
    case 6:
      interval = 'monthly'
      break
    case 3:
    case 7:
      interval = 'yearly'
      break
    }

    setData(
      {
        ...data, 
        week_days: weekDates.filter((_, index) => selectedDate.includes(index)),
        occurrences: checkedEnd === 'occurrences' ? afterNumber : null,
        end_after: checkedEnd,
        repeat_every: repeatNumber,
        interval: interval,
        end_date: checkedEnd === 'date' ? format(selectedADate, 'yyyy-MM-dd') : null,
        week_num: weekNum
      }
    )

    setDone(true)
      
    onClose()
  }

  return (
    <>
      <div className={classNames(`${className ?? ''}`, 'px-4 py-5 flex flex-col grow text-gray-900')}>
        <div className="mb-[17px] flex justify-between">
          <div className="text-bodyBold">Custom recurrence</div>
          <AptiveIcon
            icon="x"
            className="w-[20px] h-[20px] stroke-[#3D3D3D] fill-none cursor-pointer"
            onClick={() => onCancel()}
          />
        </div>
        <label className="text-small mb-[13px]">Repeat every</label>
        <div className="text-sm flex flex-row gap-x-4 items-center mb-6 z-20">
          <div className="flex flex-row items-center">
            <button className="w-[37px] h-[37px] py-[5px] px-3 border bg-ui-gray-100 rounded-l-md" onClick={() => handleSetRepeatNumber(repeatNumber, false)}>-</button>
            <input className="w-[37px] h-[37px] text-center border bg-ui-gray-100 px-[5px] py-[5px] focus:outline-none" type="text" value={repeatNumber} onChange={() => { }} required />
            <button className="w-[37px] h-[37px] py-[5px] px-3 border bg-ui-gray-100 rounded-r-md" onClick={() => handleSetRepeatNumber(repeatNumber, true)}>+</button>
          </div>
          <div className="relative w-full z-10">
            <Button
              className="w-full bg-ui-gray-100 border flex justify-between"
              icon="chevronDown"
              position="right"
              variant="neutral"
              onClick={(event) => {
                event.stopPropagation()
                setIsOpen(!isOpen)
              }}
              isFilled
              aria-label="Dropdown button"
              aria-haspopup="true"
            >
              {repeatTimes[selectedPeriod]}
            </Button>
            {
              handleSelectTimes()
            }
          </div>
        </div>

        {
          (selectedPeriod === 1 || selectedPeriod === 5) && 
          <div>
            <div className="text-small mb-[13px]">Repeat on</div>
            <div className="flex gap-2 mb-6">
              {
                weekDates.map((date: string, index: number) => {
                  return <Button key={index} className="uppercase" size="small" variant={selectedDate.includes(index) ? 'default' : 'neutral'} onClick={() => handleSeletedDate(index)}>{date.at(0)}</Button>
                })
              }
            </div>
          </div>
        }

        {
          (selectedPeriod === 2 || selectedPeriod === 6) &&
          <div className="relative w-[300px] z-10 mb-6">
            <Button
              className="w-full bg-ui-gray-100 border flex justify-between"
              icon="chevronDown"
              position="right"
              variant="neutral"
              onClick={(event) => {
                event.stopPropagation()
                setIsMonthlyRecurrenceOpen(!isMonthlyRecurrenceOpen)
              }}
              isFilled
              aria-label="Dropdown button"
              aria-haspopup="true"
            >
              { monthlyRecurrenceDetail(weekNum) }
            </Button>
            {
              handleMonthlyRecurrence()
            }
          </div>
        }
      
        <div className="text-small mb-[13px]">Ends</div>
        <div className="flex flex-col gap-2">
          <div>
            <Radio checked={checkedEnd === 'never'} onChange={() => handleSelectEndType('never')}>Never</Radio>
          </div>
            
          <div className="flex justify-between">
            <Radio checked={checkedEnd === 'date'} onChange={() => handleSelectEndType('date')}>On</Radio>
            <Popover
              open={isDatePickerShown}
              content={
                <div
                  className="bg-white p-[10px] rounded-[6px]"
                  style={{ boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.15)' }}
                >
                  <MiniCalendar
                    view="month"
                    currentDate={selectedADate}
                    onChange={handleChangeSelectedDate}
                  />
                </div>
              }
              position="bottom"
              onClickOutside={toggleDatePickerShown}
              className="right-0"
            >
              <div
                className="min-[400px]:w-[206px] bg-ui-gray-100 rounded-md border py-2 px-3 h-[37px] flex items-center cursor-pointer focus:outline-none"
                onClick={toggleDatePickerShown}
              >
                <span className={`text-[14px] font-normal leading-4 ${checkedEnd === 'date' ? 'text-[#3D3D3D]' : 'text-ui-gray-300'}`}>{format(selectedADate, 'MMMM d, yyyy')}</span>
              </div>
            </Popover>
          </div>

          <div className="flex justify-between">
            <Radio checked={checkedEnd === 'occurrences'} onChange={() => handleSelectEndType('occurrences')}>After</Radio>
            <div className="w-[50%] flex flex-row items-center">
              <button className="w-[37px] h-[37px] px-3 border bg-ui-gray-100 rounded-l-md disabled:text-ui-gray-500 text-[20px] text-[#1F2A37] font-medium" onClick={() => setAfterNumber(afterNumber - 1)} disabled={checkedEnd !== 'occurrences'} >-</button>
              <input className="w-[calc(100%-70px)] h-[37px] text-right border bg-ui-gray-100 px-3 py-[5px] focus:outline-none disabled:text-ui-gray-300 text-[#1F2A37]" type="text" value={afterNumber} onChange={() => { }} required disabled={checkedEnd !== 'occurrences'} />
              <button className="w-[37px] h-[37px] px-3 border bg-ui-gray-100 rounded-r-md disabled:text-ui-gray-500 text-[20px] text-[#1F2A37] font-medium" onClick={() => setAfterNumber(afterNumber + 1)} disabled={checkedEnd !== 'occurrences'} >+</button>
            </div>
          </div>

        </div>
      </div>

      <div className="p-3 w-full flex gap-2 justify-center bottom-3">
        <Button className="w-full h-[41px]" variant="neutral" onClick={() => onCancel()}>Cancel</Button>
        <Button className="w-full h-[41px]" onClick={() => passData()}>Done</Button>
      </div>
    </>
  )
})

export default CustomRecurrence
